import React, { Component } from 'react';
import ReactDom from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';
import { Gateway } from 'react-gateway';
import { get, castArray, throttle } from 'lodash';
import { BbButton, BbPopup } from '@browsbox-ui';
import uniqid from 'uniqid';
import {
  loadMedia,
  uploadMedia,
  startUploadFile,
  updateUploadProgress,
  mediaUploadError,
  mediaLibraryIncreasePage,
  mediaLibrarySetSearchPhrase,
} from '../../actions/media';
import { setMediaPickerType } from '../../actions/mediaManager';
import { closeDeleteMedia, deleteMedia } from '../../actions/mediaDelete';
import { setMediaOption, closeRenameMedia, renameMedia } from '../../actions/mediaConfig';
import { ROUTE_MEDIA_GET, ROUTE_MEDIA_GET_LARGE } from '../../api/routes';
import ModalFooter from '../Modals/ModalFooter';
import ImageList from './ImageList';
import ConfirmationModal from '../Modals/ConfirmationModal';
import PromptModal from '../Modals/PromptModal';
import { FormInput } from '../FormElements';
import MEDIA_TYPES from './MediaTypes';
import i18n from '../../internationalization/i18n';
import Skeleton from '../Loader';
import { selectMediaItems, selectUploadedItems } from '../../selectors/media';
import Icon from '../Icon/Icon';
import Media from './Media';

const propTypes = {
  deleteMedia: PropTypes.func.isRequired, // Redux action creator
  setMediaOption: PropTypes.func.isRequired, // Redux action creator
  setMediaPickerType: PropTypes.func.isRequired, // Redux action creator
  renameMedia: PropTypes.func.isRequired, // Redux action creator
  closeDeleteMedia: PropTypes.func.isRequired, // Redux action creator
  renameMediaProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired, // from Redux store
  isRenameMediaModalOpen: PropTypes.bool.isRequired, // from Redux store
  deleteMediaProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired, // from Redux store
  isDeleteMediaModalOpen: PropTypes.bool.isRequired, // from Redux store
  loadMedia: PropTypes.func.isRequired, // Redux action creator
  uploadMedia: PropTypes.func.isRequired, // Redux action creator
  startUploadFile: PropTypes.func.isRequired,
  updateUploadProgress: PropTypes.func.isRequired,
  mediaUploadError: PropTypes.func.isRequired,
  media: PropTypes.array.isRequired, // Media data
  mediaId: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]), // Media data
  onCancel: PropTypes.func.isRequired, // Modal data
  onOk: PropTypes.func.isRequired, // Modal data
  submitButtonText: PropTypes.string, // Modal data
  closeRenameMedia: PropTypes.func.isRequired, // Redux action creator
  mediaLibraryIncreasePage: PropTypes.func.isRequired, // Redux action creator
  mediaLibrarySetSearchPhrase: PropTypes.func.isRequired, // Redux action creator
  searchPhrase: PropTypes.string, // filter media by type and hide type filter
  mediaManagerContentType: PropTypes.string, // filter media by type and hide type filter
  showTypeFilter: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    pages: PropTypes.number.isRequired,
    perPage: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    hasMorePages: PropTypes.bool.isRequired,
    isFirstPage: PropTypes.bool.isRequired,
    isLastPage: PropTypes.bool.isRequired,
  }).isRequired,
};

const defaultProps = {
  iconUrl: '',
  mediaId: false,
  submitButtonText: null,
  searchPhrase: '',
  showTypeFilter: false,
  mediaManagerContentType: null,
};

const FilterTypes = [
  { key: 'all', labelTranslationKey: 'CONTENT.showAll', value: '' },
  { key: MEDIA_TYPES.document, labelTranslationKey: 'CONTENT.documents', value: MEDIA_TYPES.document },
  { key: MEDIA_TYPES.image, labelTranslationKey: 'CONTENT.images', value: MEDIA_TYPES.image },
];

const ADDITIONAL_IMAGE_TYPES = [
  'image',
  'gallery',
  'galleryImage',
  'sliderImage',
];

class MediaManager extends Component {
  constructor(props) {
    super(props);
    this.dropzoneRef = ''; // Reference to mounted dropzone
    this.onOk = this.onOk.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onFileDrop = this.onFileDrop.bind(this);
    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.triggerFileUpload = this.triggerFileUpload.bind(this);
    this.onDropzoneMounted = this.onDropzoneMounted.bind(this);
    this.onDeleteConfirmation = this.onDeleteConfirmation.bind(this);
    this.onRenameConfirmation = this.onRenameConfirmation.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onFilenameFilter = this.onFilenameFilter.bind(this);
    this.onTypeFilter = this.onTypeFilter.bind(this);
    this.showTypeSelector = this.showTypeSelector.bind(this);
    this.hideTypeSelector = this.hideTypeSelector.bind(this);
    this.renderTypeSelectorItem = this.renderTypeSelectorItem.bind(this);
    this.isMultipleMediaSelection = this.isMultipleMediaSelection.bind(this);
    this.onUploadProgress = this.onUploadProgress.bind(this);
    this.handleContentScroll = throttle(this.handleContentScroll, 500);

    this.state = {
      hasSelection: this.hasSelection(props.mediaId), // has media item selected
      selectedMediaId: props.mediaId, // id of the selected media item
      droppedFiles: [], // files to upload by drag-and-drop
      dropzoneActive: false, // hover over dropzone is active
      uploadActive: false, // hover over dropzone is active
      media: props.media, // list of media files to filter
      showTypeSelector: false, // show media type filter selector
      isLoading: true, // loading media list
    };
  }

  componentDidMount() {
    this.isValid = true;
    this.props.loadMedia().then(() => {
      if (this.isValid) {
        this.setState({ isLoading: false });
      }
    });
  }

  componentWillUnmount() {
    Media.flushListeners({ canceled: true, media: null });
    this.isValid = false;
  }

  // Handle filter input element value change.
  // The name of the input element should match the state filter property name
  onFilenameFilter(evt) {
    const { target: { value } } = evt;
    this.props.mediaLibrarySetSearchPhrase(value);
  }

  // Handle media type filter value change.
  onTypeFilter(evt, value) {
    evt.preventDefault();
    this.props.setMediaPickerType(value);
  }

  onDragEnter() {
    this.setState({
      dropzoneActive: true,
    });
  }

  onDragLeave() {
    this.setState({
      dropzoneActive: false,
    });
  }

  onUploadStart(uploadedFile, uniqId) {
    const file = {
      name: uploadedFile.name,
      lastModified: uploadedFile.lastModified,
      size: uploadedFile.size,
      type: uploadedFile.type,
    };

    this.props.startUploadFile(file, uniqId);
  }

  onUploadProgress(uploadedFile, event, uniqId) {
    const progress = Math.round((event.loaded * 100) / event.total);
    const file = {
      name: uploadedFile.name,
      lastModified: uploadedFile.lastModified,
      size: uploadedFile.size,
      type: uploadedFile.type,
    };

    this.props.updateUploadProgress(file, progress, uniqId);
  }

  onUploadError(xhr, uniqId) {
    const message = get(xhr, 'error.response.data.message', 'Failed to upload file');
    this.props.mediaUploadError(uniqId, message);
  }

  onBeforeStartFilesUpload() {
    if (!this.contentRef) {
      return;
    }

    this.contentRef.scrollTo({
      top: 0,
      behavior: 'smooth',
      duration: 300,
    });
  }

  // Start uploading files
  onFileDrop(files) {
    this.setState({
      dropzoneActive: false,
    });

    if (files.length) {
      this.setState({
        droppedFiles: files,
      });

      this.onBeforeStartFilesUpload({ files });

      files.map((file) => {
        const uploadId = uniqid();
        this.onUploadStart(file, uploadId);

        const metadata = { uploadId };
        return this.props.uploadMedia(file, {
          onUploadProgress: event => this.onUploadProgress(file, event, uploadId),
          onError: xhr => this.onUploadError(xhr, uploadId),
        }, metadata)
          .then((response) => {
            const uploadedId = get(response, 'response.id');

            if (uploadedId) {
              if (this.isMultipleMediaSelection()) {
                const prev = Array.from(this.state.selectedMediaId || []);
                this.onChange([
                  ...prev,
                  uploadedId,
                ]);
              } else {
                this.onChange(uploadedId);
              }
            }
          });
      });
    }
  }

  onOk() {
    const {
      media,
      mediaManagerContentType,
    } = this.props;
    const {
      selectedMediaId,
    } = this.state;

    castArray(selectedMediaId).map((mediaId) => {
      const selectedMedia = media.filter(m => m.id === mediaId).pop() || false;
      const mediaItem = Object.assign({}, selectedMedia);
      const { id, filename, fileExtension } = mediaItem;
      const path = `${id}/${encodeURIComponent(filename)}${fileExtension}`;
      mediaItem.url = `${ROUTE_MEDIA_GET}/${path}`;
      mediaItem.urlLarge = `${ROUTE_MEDIA_GET_LARGE}/${path}`;

      Media.flushListeners({ canceled: false, media: mediaItem });
      return this.props.onOk(mediaItem, mediaManagerContentType);
    });

    // running through all images and resetting the src attribute
    // allows us to cancel images requests if there are lots of them.
    // eslint-disable-next-line react/no-find-dom-node
    const imageList = ReactDom.findDOMNode(this.imageListRef);
    if (!imageList) {
      return;
    }

    const images = Array.from(imageList.querySelectorAll('img'));
    images.map(image => image.setAttribute('src', ''));
  }

  onCancel(evt) {
    evt.preventDefault();
    this.props.onCancel();
  }

  onChange(mediaId) {
    this.setState({
      hasSelection: this.hasSelection(mediaId),
      selectedMediaId: mediaId,
    });
  }

  onDoubleClick(mediaId) {
    if (!this.isMultipleMediaSelection()) {
      this.setState({
        hasSelection: this.hasSelection(mediaId),
        selectedMediaId: mediaId,
      }, () => {
        this.onOk();
      });
    }
  }

  onDropzoneMounted(ref) {
    if (ref) {
      this.dropzoneRef = ref;
    }
  }

  // On confirmation of delete media modal
  onDeleteConfirmation() {
    const { deleteMediaProps } = this.props;
    this.props.deleteMedia(deleteMediaProps)
      .then((response) => {
        const { error } = response;
        if (!error) {
          this.props.loadMedia();
        }
      });
    // if media to be deleted is selected media, unselect media
    if (this.state.selectedMediaId === deleteMediaProps.id) {
      this.setState({
        hasSelection: false,
        selectedMediaId: null,
      });
    }
    this.closeModal();
  }

  // On confirmation of rename media modal
  onRenameConfirmation(options) {
    const { renameMediaProps: { id } } = this.props;
    const { value } = options;
    const option = 'filename';
    this.props.renameMedia({ id, value })
      .then((response) => {
        const { error } = response;
        if (!error) {
          this.props.setMediaOption({ id, option, value });
          this.props.loadMedia();
        }
      });
    this.closeModal();
  }

  onContentScroll = (event) => {
    event.persist();
    this.handleContentScroll(event);
  };

  getAcceptedFileTypes = () => {
    const { mediaManagerContentType } = this.props;

    if (ADDITIONAL_IMAGE_TYPES.includes(mediaManagerContentType)) {
      return 'image/*';
    }

    return null;
  };

  hasSelection = (selection) => {
    if (Array.isArray(selection)) {
      return Boolean(selection.length);
    }

    return Boolean(selection);
  };

  showTypeSelector(evt) {
    evt.preventDefault();
    this.setState({
      showTypeSelector: true,
    });
  }

  hideTypeSelector() {
    this.setState({
      showTypeSelector: false,
    });
  }

  // Close confirmation modals
  closeModal() {
    if (this.props.isDeleteMediaModalOpen) {
      this.props.closeDeleteMedia();
    }
    if (this.props.isRenameMediaModalOpen) {
      this.props.closeRenameMedia();
    }
  }

  triggerFileUpload(evt) {
    if (evt) {
      evt.preventDefault();
    }
    // show OS "file selector"
    this.dropzoneRef.open();
  }

  isMultipleMediaSelection() {
    const { mediaManagerContentType } = this.props;
    const multipleTypes = [
      'gallery',
    ];

    return mediaManagerContentType && multipleTypes.includes(mediaManagerContentType);
  }

  handleContentScroll(event) {
    const target = event.target;
    const scrollThreshold = 0.75; // when 75% of scroll is achieved raise a new page
    const { pagination } = this.props;

    if (pagination.hasMorePages && target.offsetHeight + target.scrollTop >= (target.scrollHeight * scrollThreshold)) {
      this.props.mediaLibraryIncreasePage();
    }
  }

  renderModal() {
    const {
      deleteMediaProps,
      isDeleteMediaModalOpen,
      isRenameMediaModalOpen,
      renameMediaProps,
    } = this.props;
    // Show "delete media" modal
    if (isDeleteMediaModalOpen) {
      return (
        <ConfirmationModal
          icon="icon-basic_sheet"
          onCancel={this.closeModal}
          onClose={this.closeModal}
          onOk={this.onDeleteConfirmation}
          submitButtonText={i18n.t('CONTENT.delete')}
          title={i18n.t('CONTENT.deleteMediaTitle')}
        >
          <p className="u-mb-l u-mt-l">{i18n.t('CONTENT.deleteMediaQuestion')}</p>
          <p className="u-mb-l u-mt-l"><strong>{deleteMediaProps.filename}{deleteMediaProps.fileExtension}</strong></p>
        </ConfirmationModal>
      );
    }
    // Show "rename media" modal
    if (isRenameMediaModalOpen) {
      return (
        <PromptModal
          icon="icon-basic_sheet"
          onCancel={this.closeModal}
          onClose={this.closeModal}
          onOk={this.onRenameConfirmation}
          submitButtonText={i18n.t('CONTENT.update')}
          title={i18n.t('CONTENT.updateMediaName')}
          value={renameMediaProps.filename}
          label={i18n.t('CONTENT.newMediaName')}
        />
      );
    }

    return null;
  }

  renderFilenameFilter() {
    const {
      searchPhrase,
    } = this.props;
    return (
      <FormInput
        placeholder={i18n.t('CONTENT.search')}
        value={searchPhrase}
        onChange={this.onFilenameFilter}
        type="search"
        className="c-bb-media-manager__search-input"
      />
    );
  }

  renderTypeFilter() {
    const tetherTarget = 'mediamanager-typefilter';
    const {
      showTypeSelector,
    } = this.state;
    return (
      <div>
        <a onClick={this.showTypeSelector} className="o-action-link" id={tetherTarget}>
          <Icon name="filter" />
        </a>
        { showTypeSelector && this.renderTypeSelector(tetherTarget) }
      </div>
    );
  }

  renderUpload() {
    return (
      <a
        onClick={this.triggerFileUpload}
        className="o-bb-link o-bb-link--action c-bb-media-manager__action-item"
      >
        <Icon name="add" />
        {i18n.t('CONTENT.fileUpload')}
      </a>
    );
  }

  renderTypeSelectorItem(item) {
    const { mediaManagerContentType } = this.props;
    const { value, labelTranslationKey, key } = item;
    const label = i18n.t(labelTranslationKey);

    if (mediaManagerContentType === value) {
      return (
        <span key={key} className="o-bb-popup__action o-bb-popup__action--active">
          {label}
        </span>
      );
    }

    return (
      <a
        key={key}
        className="o-bb-popup__action"
        onClick={evt => this.onTypeFilter(evt, value)}
      >
        {label}
      </a>
    );
  }

  renderTypeSelector(tetherTarget) {
    const popupClasses = 'o-bb-popup--config';
    return (
      <Gateway into="popup">
        <BbPopup
          target={tetherTarget}
          className={popupClasses}
          closeOnClickInside
          placement="bottom center"
          onClose={this.hideTypeSelector}
          // The popup and modal both try to set focus on there element causing issues. Do not
          // focus popup within the modal
          focus={false}
        >
          <div>
            <div>
              { FilterTypes.map(this.renderTypeSelectorItem) }
            </div>
          </div>
        </BbPopup>
      </Gateway>
    );
  }

  renderLoader = () => (
    <ul className="c-bb-media-manager__list">
      {
        Array.from(Array(4).keys()).map(key => (
          <li className="c-bb-media-manager__list-item" key={key}>
            <Skeleton
              height="247px"
              widthRandomness={0}
            />
          </li>
        ))
      }
    </ul>
  );

  renderEmpty = () => {
    const { searchPhrase } = this.props;

    if (searchPhrase) {
      return (
        <div className="c-bb-media-manager__no-result">
          {i18n.t('CONTENT.noResults')}
        </div>
      );
    }

    // Media list filter result is empty
    return (
      <div className="c-bb-media-manager__no-result">
        {i18n.t('CONTENT.mediaAddFirstItem')}
      </div>
    );
  };

  render() {
    const {
      hasSelection,
      dropzoneActive,
      uploadActive,
      selectedMediaId,
      isLoading,
    } = this.state;
    const {
      media,
      showTypeFilter,
    } = this.props;

    const icon = 'icon-upload';
    const submitButtonText = this.props.submitButtonText || i18n.t('CONTENT.save');
    const hasNoMedia = media.length === 0;

    return (
      <div className="c-bb-media-manager__wrapper">
        <div className="c-bb-media-manager__actions">
          { this.renderUpload() }
          <div className="c-bb-media-manager__actions-right">
            { this.renderFilenameFilter() }
            { showTypeFilter && this.renderTypeFilter() }
          </div>
        </div>
        <div
          ref={(node) => { this.contentRef = node; }}
          className="o-bb-modal__content"
          onScroll={this.onContentScroll}
        >
          <Dropzone
            onDrop={this.onFileDrop}
            onDragEnter={this.onDragEnter}
            onDragLeave={this.onDragLeave}
            accept={this.getAcceptedFileTypes()}
            disableClick
            disablePreview
            ref={this.onDropzoneMounted}
            className="c-bb-media-manager__dropzone"
          >
            {
              (!isLoading && !hasNoMedia) &&
              <ImageList
                items={media}
                selected={selectedMediaId}
                onChange={this.onChange}
                onDoubleClick={this.onDoubleClick}
                multiple={this.isMultipleMediaSelection()}
                ref={(node) => { this.imageListRef = node; }}
              />
            }
            { (!isLoading && hasNoMedia) && this.renderEmpty() }
            { dropzoneActive &&
              <div className="c-bb-media-manager__dropzone-overlay">
                <i className={icon} /> {i18n.t('CONTENT.fileDrop')}
              </div>
            }
            { (isLoading || uploadActive) && this.renderLoader() }
          </Dropzone>
          {this.renderModal()}
        </div>
        <ModalFooter>
          <BbButton className="c-bb-button--primary" disabled={!hasSelection} onClick={this.onOk} text={submitButtonText} />
          <BbButton className="c-bb-button--secondary" onClick={this.onCancel} text={i18n.t('CONTENT.cancel')} />
        </ModalFooter>
      </div>
    );
  }
}

MediaManager.propTypes = propTypes;
MediaManager.defaultProps = defaultProps;

const mapStateToProps = (state) => {
  const {
    content: {
      deleteMediaProps,
      isDeleteMediaModalOpen,
      isRenameMediaModalOpen,
      renameMediaProps,
      mediaManagerContentType,
    },
    media: {
      library: {
        searchPhrase,
      },
    },
  } = state;

  const { items, pagination } = selectMediaItems(state);

  return {
    deleteMediaProps,
    isDeleteMediaModalOpen,
    isRenameMediaModalOpen,
    media: [
      ...selectUploadedItems(state),
      ...items,
    ],
    renameMediaProps,
    mediaManagerContentType,
    searchPhrase,
    pagination,
  };
};

const mapDispatchToProps = {
  closeDeleteMedia,
  closeRenameMedia,
  deleteMedia,
  loadMedia,
  setMediaOption,
  setMediaPickerType,
  renameMedia,
  uploadMedia,
  startUploadFile,
  updateUploadProgress,
  mediaUploadError,
  mediaLibraryIncreasePage,
  mediaLibrarySetSearchPhrase,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MediaManager);



// WEBPACK FOOTER //
// ./src/components/MediaManager/MediaManagerContainer.js